import { useState, useCallback, useRef } from 'react';
import { history } from '@umijs/max';

export interface TabItem {
    key: string;
    pathname: string;
    title: string;
    keepAliveName?: string;
    closable?: boolean;
}

export default () => {
    const [tabList, setTabList] = useState<TabItem[]>([]);
    const [activeKey, setActiveKey] = useState<string>('');

    // 用 ref 同步最新值，供 closeAndSwitchTab 在函数体（非 updater）中读取，
    // 避免在 setState updater 内部调用副作用导致 React 警告。
    const tabListRef = useRef<TabItem[]>(tabList);
    const activeKeyRef = useRef<string>(activeKey);
    tabListRef.current = tabList;
    activeKeyRef.current = activeKey;

    const addTab = useCallback((tab: TabItem) => {
        setTabList((prev) => {
            if (prev.some((item) => item.key === tab.key)) {
                return prev.map(item => item.key === tab.key ? { ...item, ...tab } : item);
            }
            if (tab.pathname === '/dashboard/workplace') {
                return [tab, ...prev];
            }
            return [...prev, tab];
        });
    }, []);

    const removeTab = useCallback((key: string) => {
        setTabList((prev) => prev.filter((item) => item.key !== key));
    }, []);

    const removeOtherTabs = useCallback((key: string) => {
        setTabList((prev) => prev.filter((item) => item.key === key || item.closable === false));
    }, []);

    const removeLeftTabs = useCallback((key: string) => {
        setTabList((prev) => {
            const currentIndex = prev.findIndex((item) => item.key === key);
            if (currentIndex === -1) return prev;
            return prev.filter((item, index) => index >= currentIndex || item.closable === false);
        });
    }, []);

    const removeRightTabs = useCallback((key: string) => {
        setTabList((prev) => {
            const currentIndex = prev.findIndex((item) => item.key === key);
            if (currentIndex === -1) return prev;
            return prev.filter((item, index) => index <= currentIndex || item.closable === false);
        });
    }, []);

    const removeAllTabs = useCallback(() => {
        setTabList((prev) => prev.filter((item) => item.closable === false));
    }, []);

    const closeAndSwitchTab = useCallback((path: string, callback?: () => void) => {
        const prev = tabListRef.current;
        const currentActiveKey = activeKeyRef.current;

        const targetIndex = prev.findIndex((item) => item.key === path);
        if (targetIndex === -1) {
            callback?.();
            return;
        }

        const newTabList = prev.filter((item) => item.key !== path);
        setTabList(newTabList);

        if (path === currentActiveKey) {
            let nextIndex = targetIndex - 1;
            if (nextIndex < 0) nextIndex = 0;

            if (newTabList.length > 0) {
                const nextTab = newTabList[nextIndex];
                setActiveKey(nextTab.key);
                history.push(nextTab.pathname);
            } else {
                setActiveKey('');
                history.push('/');
            }
        }

        callback?.();
    }, []);

    return {
        tabList,
        activeKey,
        setActiveKey,
        addTab,
        removeTab,
        removeOtherTabs,
        removeLeftTabs,
        removeRightTabs,
        removeAllTabs,
        closeAndSwitchTab,
        setTabList,
    };
};
